import { toast } from "react-toastify";
import { AvailabilityDto, BookingDto, RebookRequestDto } from "../Types/BookingServiceTypes";
import { DateOnly, TimeOnly } from "../Utils/DateManagement";
import { GlobalObjectMapping } from "../Utils/GlobalObjectMapping";
import { AdminTimeSlot } from "../Pages/AdminPages/AdminBookings";

import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  apiRawPut,
  apiRawDelete,
  apiRawPost,
  apiPublicGet,
  apiPublicPost,
  apiPublicRawPut,
  apiPublicRawPost
} from "./apiClient";

/* ============================================================
   PUBLIC — Accept rebook proposal
============================================================ */
const AcceptRebookProposal = async (requestId: number, slotId: number): Promise<boolean> => {
  try {
    const res = await apiPublicRawPut(
      `/Booking/rebook/accept/${requestId}/${slotId}`
    );

    if (res.status === 204) {
      toast.success("Din nya tid har bekräftats!");
      return true;
    } else {
      toast.warning(
        "Det gick inte att bekräfta den nya tiden. Länken kan vara ogiltig eller ha gått ut."
      );
      return false;
    }
  } catch (err) {
    console.error("Error when accepting rebook:", err);
    toast.error("Ett tekniskt fel uppstod när vi försökte bekräfta tiden.");
    return false;
  }
};

/* ============================================================
   PUBLIC — Decline rebook proposal
============================================================ */
const DeclineRebookProposal = async (requestId: number): Promise<boolean> => {
  try {
    const res = await apiPublicRawPut(`/Booking/rebook/decline/${requestId}`);

    if (res.status === 204) {
      toast.success("Ombokningsförslaget har nekats.");
      return true;
    }

    toast.warning("Kunde inte neka ombokningsförslaget.");
    return false;
  } catch (err) {
    console.error("Error when declining rebook:", err);
    toast.error("Ett tekniskt fel uppstod när vi försökte neka tiden.");
    return false;
  }
};

/* ============================================================
   PUBLIC — Counter rebook proposal
============================================================ */
const CounterRebookProposal = async (requestId: number, slots: { date: string; startTime: string; category?: string }[]): Promise<boolean> => {
  try {
    const payload = slots.map((x) => ({ date: x.date, startTime: x.startTime, category: x.category ?? "public" }));
    const res = await apiPublicRawPut(`/Booking/rebook/counter/${requestId}`, payload);

    if (res.status === 204) {
      toast.success("Dina alternativa tider har skickats.");
      return true;
    }

    toast.warning("Kunde inte skicka alternativa tider.");
    return false;
  } catch (err) {
    console.error("Error when countering rebook:", err);
    toast.error("Ett tekniskt fel uppstod när vi försökte skicka tiderna.");
    return false;
  }
};

/* ============================================================
   PUBLIC — Send rebook proposals
============================================================ */
const SendRebookProposal = async (bookingId: number, slots: AdminTimeSlot[]): Promise<boolean> => {
  try {
    const payload = slots.map((x) => ({ date: x.date, startTime: x.time, category: x.category }));
    const res = await apiPublicRawPost(`/Booking/rebook/${bookingId}`, payload);

    if (res.status === 200) {
      toast.success("Ombokningsförslag skickade.");
      return true;
    }

    toast.warning("Kunde inte skicka ombokningsförslag.");
    return false;
  } catch (err) {
    console.error("SendRebookProposal crash:", err);
    toast.error("Fel vid försök att skicka ombokningsförslag.");
    return false;
  }
};

/* ============================================================
   PROTECTED — Update booking note
============================================================ */
const PutNote = async (id: number, currentNotes: string): Promise<boolean> => {
  try {
    const res = await apiRawPut(`/Booking/update-booking-note/${id}`, {
      notes: currentNotes
    });

    if (res.status === 204) {
      toast.success("Uppdatering av anteckningar lyckades.");
      return true;
    }

    toast.warning("Bokning kunde inte hittas eller något annat gick fel.");
    return false;
  } catch (err) {
    console.error("Error updating booking note", err);
    toast.error("Något gick fel vid uppdatering av anteckningar");
    return false;
  }
};

/* ============================================================
   PROTECTED — Delete time slot
============================================================ */
const DeleteSelectedTimeSlot = async (id: number): Promise<boolean> => {
  try {
    const res = await apiRawDelete(`/Booking/delete-timeslot/${id}`);

    if (res.status === 204) {
      toast.success("Tiden har tagits bort.");
      return true;
    }

    toast.error("Kunde inte ta bort tiden. Den kan vara bokad.");
    return false;
  } catch (err) {
    console.error("DeleteSelectedTimeSlot error", err);
    toast.error("Tekniskt fel vid borttagning av tid.");
    return false;
  }
};

/* ============================================================
   PROTECTED — Create time slots (Admin)
============================================================ */
const CreateAvailableTimeSlots = async (newSlots: AdminTimeSlot[]): Promise<boolean> => {
  try {
    const payload = newSlots.map((x) => ({ date: x.date, startTime: x.time, category: x.category }));
    const res = await apiRawPost(`/Booking/create-slots`, payload);

    const body = await res.json().catch(() => null);

    if (res.status === 201) {
      toast.success("Tider skapades.");
      return true;
    } else if (res.status === 400) {
      toast.warning(JSON.stringify(body));
      return false;
    }

    toast.error(JSON.stringify(body));
    return false;
  } catch (err) {
    console.error("CreateAvailableTimeSlots crash", err);
    toast.error("Ett fel uppstod vid skapande av tider.");
    return false;
  }
};

/* ============================================================
   PUBLIC — Get available time slots
============================================================ */
const GetAvailableTimeSlots = async (): Promise<AvailabilityDto[] | undefined> => {
  try {
    const buff = await apiPublicGet<any[]>(`/Booking/available-time-slots`);

    return buff.map((x: any) => ({
      id: x?.id ?? -1,
      date: new DateOnly(x.date),
      startTime: new TimeOnly(x.startTime),
      endTime: new TimeOnly(x.endTime),
      maxBookings: x.maxBookings,
      category: x.category ?? "public",
      createdAt: new Date(x.createdAt),
      updatedAt: new Date(x.updatedAt)
    }));
  } catch (err) {
    console.error(err);
    toast.error("Något gick fel vid hämtning av tillgängliga tider.");
    return undefined;
  }
};

/* ============================================================
   PROTECTED — Get all booked slots
============================================================ */
const GetBookedSlots = async (): Promise<BookingDto[] | undefined> => {
  try {
    const buffJson = await apiGet<any[]>(`/Booking/get-booked-slots`);
    return await GlobalObjectMapping.MapToBookingDtos(buffJson);
  } catch (err) {
    console.error("Error fetching booked slots", err);
    toast.error("Något gick fel vid hämtning av bokade tider.");
    return undefined;
  }
};

/* ============================================================
   PROTECTED — Get rebook requests (Admin)
============================================================ */
const GetRebookRequests = async (): Promise<RebookRequestDto[] | undefined> => {
  try {
    const buffJson = await apiGet<any[]>(`/Booking/rebook/requests`);

    return await Promise.all(buffJson.map(async (x) => ({
      id: x?.id,
      bookingId: x?.bookingId,
      createdAt: x?.createdAt,
      expiresAt: x?.expiresAt,
      isClosed: x?.isClosed,
      status: x?.status,
      booking: (await GlobalObjectMapping.MapToBookingDtos(x?.booking ? [x.booking] : []))[0],
      proposedSlots: (x?.proposedSlots ?? []).map((slot: any) => ({
        id: slot?.id,
        rebookRequestId: slot?.rebookRequestId,
        date: slot?.date,
        startTime: slot?.startTime,
        endTime: slot?.endTime,
        source: slot?.source ?? "admin",
        expiresAt: slot?.expiresAt
      }))
    })));
  } catch (err) {
    console.error("Error fetching rebook requests", err);
    toast.error("Något gick fel vid hämtning av ombokningar.");
    return undefined;
  }
};

/* ============================================================
   EXPORT SERVICE
============================================================ */
export const BookingService = {
  GetAvailableTimeSlots,     // PUBLIC
  GetBookedSlots,            // PROTECTED
  GetRebookRequests,         // PROTECTED
  CreateAvailableTimeSlots,  // PROTECTED
  DeleteSelectedTimeSlot,    // PROTECTED
  PutNote,                   // PROTECTED
  SendRebookProposal,        // PUBLIC
  AcceptRebookProposal,      // PUBLIC
  DeclineRebookProposal,     // PUBLIC
  CounterRebookProposal      // PUBLIC
};
